import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PortalUsuarioService } from '../../../services/portalUsuario.service';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import {  AuthService, FormGroupErrorMessageComponent, LoadingPanelService,
  LocalService, noWhitespaceValidator, PASSWORD_PATTERN,
  Utils, VisorComponent, AlertService, AlertIcon } from 'lib-base';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { MessageService } from 'primeng/api';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { ActivarUsuarioRequest } from '../../../shared/interfaces/seguridad';
import { DialogModule } from 'primeng/dialog';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { RegistrarFirmaElectronicaComponent } from '../../firma-electronica/registrar-firma-electronica.component';

@Component({
  selector: 'app-activar-usuario',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, FormGroupErrorMessageComponent, PasswordModule, ButtonModule, DividerModule,
    DialogModule, RegistrarFirmaElectronicaComponent
  ],
  templateUrl: './activar-usuario.component.html',
  styleUrl: './activar-usuario.component.scss'
})
export class ActivarUsuarioComponent implements OnInit, OnDestroy {

  private _codigo !: string;
  isCodeValid: boolean = false;

  form !: FormGroup;

  translations: any = {};
  recaptchaSubscription !: Subscription;
  @ViewChild(RegistrarFirmaElectronicaComponent) registrarFirmaElectronica !: RegistrarFirmaElectronicaComponent;

  constructor(private _route: ActivatedRoute, private _pu: PortalUsuarioService, private _r: Router, private _fb: FormBuilder,
    private _t: TranslateService, private _ms: MessageService, private _ls: LocalService,  private _a: AuthService,
    private recaptchaV3Service: ReCaptchaV3Service, private _lps: LoadingPanelService,
    private readonly alertService:AlertService) {
    this._codigo = this._route.snapshot.params['codigo'];
    this.configureForm();
  }

  ngOnInit() {
    this.translate().then(() => {
      this.isCodigoActivacionValido();
    });
  }

  ngOnDestroy(): void {
    if (this.recaptchaSubscription) {
      this.recaptchaSubscription?.unsubscribe();
    }
  }

  private async translate(): Promise<void> {
    this.translations = await firstValueFrom(forkJoin({
      completeCampos: this._t.get('form.msg.complete-campos'),
      warnSummary: this._t.get('form.msg.warn-summary'),
      registrarExito: this._t.get('components.app-authentication-manager.register-form.registrar-exito'),
      aceptar: this._t.get('form.button.aceptar'),
      siguiente: this._t.get('form.button.siguiente'),
      campoRequerido: this._t.get('form.msg.campo-requerido'),
      sugerencias: this._t.get('components.app-activar-usuario.sugerencias'),
      contrasena: this._t.get('form.password.contrasena'),
      constasenaNoCoiniciden: this._t.get('form.password.constasena-no-coiniciden'),
      repitaContrasena: this._t.get('form.password.repita-contraseña'),
      alMenosUnaMinuscula: this._t.get('form.password.al-menos-una-minuscula'),
      alMenosUnaMayuscula: this._t.get('form.password.al-menos-una-mayuscula'),
      alMenosUnNumero: this._t.get('form.password.al-menos-un-número'),
      minimoCaracteres: this._t.get('form.password.minimo-caracteres'),
      maximoCaracteres: this._t.get('form.password.maximo-caracteres'),
      alMenosUnCaracterEspecial: this._t.get('form.password.al-menos-un-caracter-especial'),
      cambiarContrasena: this._t.get('form.password.cambiar-contrasena'),
      visorDialogTitle: this._t.get('components.app-activar-usuario.visorDialog-title'),
      codigoActivacionValidoNoValido: this._t.get('components.app-activar-usuario.codigo-activacionValido-no-valido'),
      registrarSolicitudTitle: this._t.get('components.app-activar-usuario.registrar-solicitud-title'),
      registrarSolicitudHtml: this._t.get('components.app-activar-usuario.registrar-solicitud-html'),
      cancelar: this._t.get('form.button.cancelar'),
    }));
  }

  //#region Activar Usuario
  configureForm() {
    this.form = this._fb.group({
      password: [null, [Validators.required, noWhitespaceValidator, Validators.pattern(PASSWORD_PATTERN), Validators.maxLength(200)]],
      passwordRepeat: [null, [Validators.required, noWhitespaceValidator]],
    }, { validators: this.passwordMatchValidator });
  }

  isCodigoActivacionValido() {
    this._pu.isCodigoActivacionValido(this._codigo).subscribe({
      next: (response: boolean) => {

        this.isCodeValid = response;
        if (response) {
          this.configureForm();
          return;
        }
        this.alertService.show({
          text: this.translations.codigoActivacionValidoNoValido,
          icon: AlertIcon.ERROR,
          showAccept: true,
           textAccept: this.translations.siguiente,
          showCancel: false,
        }).subscribe(async (result) => {
          this._r.navigate(['/public/consulta']).then();
        });
      }
    });
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    return control.value.password === control.value.passwordRepeat ? null : { notMatch: true };
  }

  activarUsuario(request: ActivarUsuarioRequest) {
    this._lps.show(true);
    this._pu.activarUsuario(request).subscribe({
      next: (response: boolean) => {
        this._lps.hide();
        this.registrarFirmaElectronica.documentoSolicitudFirmaElectronica(this._codigo);
      }
    });
  }

  onSubmit() {
    if (this.form.invalid) {
      Utils.markAllFormControlsAsTouched(this.form);
      this._ms.add({ severity: 'warn', summary: this.translations.warnSummary, detail: this.translations.completeCampos });
      return;
    }

    this.recaptchaSubscription = this.recaptchaV3Service.execute('activarUsuario')
      .subscribe((token) => {
        const request = {
          codigo: this._codigo,
          newPassword: this.form.get('password')?.value,
          captchaToken: token
        } as ActivarUsuarioRequest;

        this.activarUsuario(request);
      });
  }
  //#endregion Activar Usuario
}
